import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FooterSection from '../../components/sections/FooterSection';
import NotFound from '../NotFound';

/*
 * Компонент для отображения страницы конкретного партнёра.
 *
 * ### Как использовать:
 * 1.  **Настройка роутинга:**
 *     В вашем основном файле роутинга (например, App.js) необходимо настроить динамический маршрут.
 *     Пример для React Router:
 *     `<Route path="/partners/:partnerId" element={<PartnerPage />} />`
 *
 * 2.  **Выбор партнёра:**
 *     Партнёр для отображения выбирается через параметр `partnerId` в URL.
 *     Этот `partnerId` должен соответствовать полю `id` одного из объектов в файле `/public/data/partners.json`.
 *
 * 3.  **Примеры URL:**
 *     - Для отображения Nazarbayev University (с `id: "nu"`): `/partners/nu`
 *     - Для отображения Mekgraj International (с `id: "mekgraj"`): `/partners/mekgraj`
 *
 * Компонент автоматически загрузит данные из JSON, найдёт нужного партнёра по `id` и отобразит его информацию.
 * Если партнёр с указанным `id` не будет найден, отобразится страница "Not Found".
*/

/**
 * Renders a dynamic partner information page by fetching data from a JSON file.
 * It uses the 'partnerId' from the URL to display the correct partner.
 * @returns {JSX.Element} The rendered page component for a partner, or a Not Found page.
 */
function PartnerPage() {
    const { partnerId } = useParams();
    const navigate = useNavigate();
    const [partner, setPartner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        fetch('/data/partners.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(partners => {
                const currentPartner = partners.find(p => p.id === partnerId);
                if (currentPartner) {
                    setPartner(currentPartner);
                } else {
                    setError(true); // Partner not found in the JSON
                }
            })
            .catch(err => {
                console.error("Failed to fetch partner data:", err);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [partnerId]);

    if (loading) {
        return <div className="uk-container uk-padding">Loading...</div>;
    }

    if (error || !partner) {
        return <NotFound />;
    }

    return (
        <>
            <div className="uk-section uk-section-default">
                <div className="uk-container uk-container-small">
                    <img src={partner.logoUrl} alt={`${partner.name} logo`} className="uk-align-center uk-margin-medium-bottom" style={{ maxHeight: '120px' }} />
                    <h1 className="uk-heading-medium uk-text-center">{partner.name}</h1>
                    <p className="uk-text-lead uk-text-center">{partner.description}</p>
                    <div className="uk-text-center uk-margin-large-top">
                        <a href={partner.websiteUrl} className="uk-button uk-button-jai uk-button-large" target="_blank" rel="noopener noreferrer">
                            Visit Website
                        </a>
                    </div>
                </div>
            </div>
            <FooterSection />
        </>
    );
}

export default PartnerPage;